/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 *
 * View model for the Review quality card (PLAT-1819): wire types + runtime
 * validation for GET /review-quality, and the pure aggregations the card
 * renders. Validators mirror the backend's shapes by hand, same deliberate
 * duplication as claudeMetricsView.ts (see the rationale there).
 *
 * The governing rule carries over: an aggregate is shown only when exact.
 * Invalid rows are dropped AND counted (`droppedRows`), and a truncated
 * payload keeps its flag, so the card can withhold or caveat instead of
 * quietly summing a partial answer.
 */

export type ReviewFindingRow = {
  org: string;
  repo: string;
  pr: number;
  findingId: string;
  pass: 'security' | 'satellite' | 'code' | 'language';
  advisory: boolean;
  classText: string;
  file?: string;
  line?: number;
  severity?: 'blocking' | 'major' | 'minor' | 'suggestion';
  state: 'open' | 'fixed' | 'closed' | 'declined';
  statusRound?: number;
  statusSha?: string;
  statusDetail: string;
  firstRound?: number;
  firstSha?: string;
  snapshotRound?: number;
  runId: string;
  prClosed: boolean;
  prMerged?: boolean;
  firstSeenAt: string;
  updatedAt: string;
};

export type ReviewQualityData = {
  findings: ReviewFindingRow[];
  since: string;
  truncated: boolean;
  /** Wire rows dropped by validation — non-zero means the totals are partial. */
  droppedRows: number;
};

const PASSES = ['security', 'satellite', 'code', 'language'] as const;
const SEVERITIES = ['blocking', 'major', 'minor', 'suggestion'] as const;
const STATES = ['open', 'fixed', 'closed', 'declined'] as const;

const optString = (v: unknown): string | undefined =>
  typeof v === 'string' && v ? v : undefined;
const optNumber = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;
const optBool = (v: unknown): boolean | undefined =>
  typeof v === 'boolean' ? v : undefined;

function parseFinding(value: unknown): ReviewFindingRow | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const f = value as Record<string, unknown>;
  if (
    typeof f.org !== 'string' ||
    typeof f.repo !== 'string' ||
    typeof f.pr !== 'number' ||
    typeof f.findingId !== 'string' ||
    typeof f.classText !== 'string' ||
    typeof f.statusDetail !== 'string' ||
    typeof f.runId !== 'string'
  ) {
    return undefined;
  }
  if (!(PASSES as readonly string[]).includes(f.pass as string)) return undefined;
  if (!(STATES as readonly string[]).includes(f.state as string)) return undefined;
  const severity = (SEVERITIES as readonly string[]).includes(f.severity as string)
    ? (f.severity as ReviewFindingRow['severity'])
    : undefined;
  const firstSeenAt = optString(f.firstSeenAt);
  const updatedAt = optString(f.updatedAt);
  if (!firstSeenAt || !updatedAt) return undefined;

  return {
    org: f.org,
    repo: f.repo,
    pr: f.pr,
    findingId: f.findingId,
    pass: f.pass as ReviewFindingRow['pass'],
    advisory: f.advisory === true,
    classText: f.classText,
    file: optString(f.file),
    line: optNumber(f.line),
    severity,
    state: f.state as ReviewFindingRow['state'],
    statusRound: optNumber(f.statusRound),
    statusSha: optString(f.statusSha),
    statusDetail: f.statusDetail,
    firstRound: optNumber(f.firstRound),
    firstSha: optString(f.firstSha),
    snapshotRound: optNumber(f.snapshotRound),
    runId: f.runId,
    prClosed: f.prClosed === true,
    prMerged: optBool(f.prMerged),
    firstSeenAt,
    updatedAt,
  };
}

export function parseReviewQualityResponse(body: unknown): ReviewQualityData | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const b = body as Record<string, unknown>;
  if (!Array.isArray(b.findings) || typeof b.since !== 'string') return undefined;
  const findings: ReviewFindingRow[] = [];
  let droppedRows = 0;
  for (const raw of b.findings) {
    const f = parseFinding(raw);
    if (f) findings.push(f);
    else droppedRows++;
  }
  return {
    findings,
    since: b.since,
    truncated: b.truncated === true,
    droppedRows,
  };
}

export const PASS_LABELS: Record<ReviewFindingRow['pass'], string> = {
  security: 'Security',
  satellite: 'Conventions',
  code: 'Code Review',
  language: 'Language Patterns',
};

export const SEVERITY_LABELS: Record<string, string> = {
  blocking: 'Blocking',
  major: 'Major',
  minor: 'Minor',
  suggestion: 'Suggestion',
  unclassified: 'Unclassified',
};

export const STATE_LABELS: Record<ReviewFindingRow['state'], string> = {
  open: 'Open',
  fixed: 'Fixed',
  closed: 'Closed (not re-detected)',
  declined: 'Declined',
};

/**
 * The card's headline numbers. Advisory rows (⚪ suggestions) are excluded
 * from the fix-rate denominator — they are offered, not demanded — and shown
 * as their own count. "Open at merge" needs merge knowledge, which only repos
 * whose caller sends `closed` events report: the counts stay separate so
 * absence reads as "not reported", never as zero.
 */
export type ReviewQualitySummary = {
  total: number;
  advisory: number;
  fixed: number;
  open: number;
  closed: number;
  declined: number;
  /** fixed ÷ total, the Google-style addressed rate; undefined when total is 0. */
  fixRate?: number;
  /** Median of (statusRound − firstRound) over fixed findings with both known. */
  medianRoundsToFix?: number;
  /** Findings still open on a PR known to have merged. */
  openAtMerge: number;
  /** How many findings sit on PRs with merge state reported at all. */
  withMergeState: number;
};

export function summarizeReviewQuality(rows: ReviewFindingRow[]): ReviewQualitySummary {
  const tracked = rows.filter(r => !r.advisory);
  const by = (state: ReviewFindingRow['state']) =>
    tracked.filter(r => r.state === state).length;

  const roundsToFix = tracked
    .filter(
      r =>
        r.state === 'fixed' &&
        r.statusRound !== undefined &&
        r.firstRound !== undefined &&
        r.statusRound >= r.firstRound,
    )
    .map(r => (r.statusRound as number) - (r.firstRound as number))
    .sort((a, b) => a - b);
  let medianRoundsToFix: number | undefined;
  if (roundsToFix.length) {
    const mid = Math.floor(roundsToFix.length / 2);
    medianRoundsToFix =
      roundsToFix.length % 2
        ? roundsToFix[mid]
        : (roundsToFix[mid - 1] + roundsToFix[mid]) / 2;
  }

  const withMergeState = tracked.filter(r => r.prMerged !== undefined).length;
  const openAtMerge = tracked.filter(
    r => r.state === 'open' && r.prMerged === true,
  ).length;

  const fixed = by('fixed');
  return {
    total: tracked.length,
    advisory: rows.length - tracked.length,
    fixed,
    open: by('open'),
    closed: by('closed'),
    declined: by('declined'),
    fixRate: tracked.length ? fixed / tracked.length : undefined,
    medianRoundsToFix,
    openAtMerge,
    withMergeState,
  };
}

export const formatFixRate = (rate: number | undefined): string =>
  rate === undefined ? '—' : `${Math.round(rate * 100)}%`;

/** One run's usage, from GET /runs (PLAT-1819 run drill). */
export type PrRunModel = {
  model: string;
  msgs: number;
  inputTokens: number;
  outputTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
};

export type PrRun = {
  runId: string;
  jobId: string;
  org: string;
  repo: string;
  pr?: number;
  author: string;
  trigger: string;
  /** ISO timestamp on the wire (a Date serialized by the route). */
  receivedAt: string;
  costUsd?: number;
  durationMs?: number;
  models: PrRunModel[];
};

export type PrRunsData = {
  runs: PrRun[];
  retentionWeeks: number;
  /** The server capped the union — what is shown is the oldest slice, not all. */
  truncated: boolean;
  /** Wire rows dropped by validation — non-zero means the history is partial. */
  droppedRuns: number;
};

const finiteNum = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;

function parsePrRun(value: unknown): PrRun | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const r = value as Record<string, unknown>;
  if (
    typeof r.runId !== 'string' ||
    typeof r.jobId !== 'string' ||
    typeof r.org !== 'string' ||
    typeof r.repo !== 'string' ||
    typeof r.author !== 'string' ||
    typeof r.trigger !== 'string' ||
    typeof r.receivedAt !== 'string' ||
    !Array.isArray(r.models)
  ) {
    return undefined;
  }
  const models: PrRunModel[] = [];
  for (const m of r.models) {
    if (!m || typeof m !== 'object') return undefined;
    const mm = m as Record<string, unknown>;
    if (typeof mm.model !== 'string') return undefined;
    const msgs = finiteNum(mm.msgs);
    const inputTokens = finiteNum(mm.inputTokens);
    const outputTokens = finiteNum(mm.outputTokens);
    const cacheWriteTokens = finiteNum(mm.cacheWriteTokens);
    const cacheReadTokens = finiteNum(mm.cacheReadTokens);
    if (
      msgs === undefined ||
      inputTokens === undefined ||
      outputTokens === undefined ||
      cacheWriteTokens === undefined ||
      cacheReadTokens === undefined
    ) {
      return undefined;
    }
    models.push({
      model: mm.model,
      msgs,
      inputTokens,
      outputTokens,
      cacheWriteTokens,
      cacheReadTokens,
    });
  }
  return {
    runId: r.runId,
    jobId: r.jobId,
    org: r.org,
    repo: r.repo,
    pr: finiteNum(r.pr),
    author: r.author,
    trigger: r.trigger,
    receivedAt: r.receivedAt,
    costUsd: finiteNum(r.costUsd),
    durationMs: finiteNum(r.durationMs),
    models,
  };
}

export function parsePrRunsResponse(body: unknown): PrRunsData | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const b = body as Record<string, unknown>;
  if (!Array.isArray(b.runs) || typeof b.retentionWeeks !== 'number') return undefined;
  const runs: PrRun[] = [];
  let droppedRuns = 0;
  for (const raw of b.runs) {
    const r = parsePrRun(raw);
    if (r) runs.push(r);
    else droppedRuns++;
  }
  return {
    runs,
    retentionWeeks: b.retentionWeeks,
    truncated: b.truncated === true,
    droppedRuns,
  };
}

/** Wall-clock for humans: 444000 -> "7m 24s"; sub-minute -> "42s". */
export const formatDurationMs = (ms: number): string => {
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};
